/**
 * Assets management page.
 *
 * CRUD for the approved-asset library (images, PDFs) used by AI personas.
 * Route: /assets (gated by 'inbox' feature — user-scoped, no AdminOnly).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { FileImage, Plus, Trash2, Search, Eye, Pencil, Link as LinkIcon } from 'lucide-react'
import { Spinner } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { AttributeChipInput } from '@/components/admin/AttributeChipInput'
import { AssetPreviewModal } from '@/components/assets/AssetPreviewModal'
import { AssetEditDrawer } from '@/components/assets/AssetEditDrawer'
import { formatBytes } from '@/utils/formatBytes'
import { matchesAssetSearch } from '@/utils/assetSearch'
import { listAssets, uploadAsset, createUrlAsset, deleteAsset } from '@/services/assets'
import type { ApprovedAsset } from '@/types/assets'

export function AssetsPage() {
  const [assets, setAssets] = useState<ApprovedAsset[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)

  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [previewAsset, setPreviewAsset] = useState<ApprovedAsset | null>(null)
  const [editAsset, setEditAsset] = useState<ApprovedAsset | null>(null)

  const [showLinkForm, setShowLinkForm] = useState(false)
  const [linkName, setLinkName] = useState('')
  const [linkSlug, setLinkSlug] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [linkDescription, setLinkDescription] = useState('')
  const [linkTags, setLinkTags] = useState<string[]>([])
  const [linkSaving, setLinkSaving] = useState(false)
  const [linkFormError, setLinkFormError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      setAssets(await listAssets())
    } catch {
      // silent
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const resetForm = () => {
    setFile(null); setName(''); setSlug(''); setDescription(''); setTags([]); setFormError(null)
  }

  const resetLinkForm = () => {
    setLinkName(''); setLinkSlug(''); setLinkUrl(''); setLinkDescription(''); setLinkTags([]); setLinkFormError(null)
  }

  const handleUpload = async () => {
    if (!file || !name || !slug) return
    setSaving(true); setFormError(null)
    try {
      const res = await uploadAsset(file, name, slug, description, tags)
      if (!res.ok) {
        setFormError(
          res.status === 409 ? 'That slug is already in use.'
          : res.status === 415 ? 'Unsupported file type (JPEG/PNG/WebP/PDF only).'
          : res.status === 413 ? 'File is too large.'
          : 'Upload failed. Please try again.',
        )
        return
      }
      setShowForm(false); resetForm(); refresh()
    } finally {
      setSaving(false)
    }
  }

  const handleAddLink = async () => {
    if (!linkName || !linkSlug || !linkUrl) return
    setLinkSaving(true); setLinkFormError(null)
    try {
      const body: { name: string; slug: string; url: string; description?: string; tags?: string[] } = {
        name: linkName, slug: linkSlug, url: linkUrl,
      }
      if (linkDescription) body.description = linkDescription
      if (linkTags.length > 0) body.tags = linkTags
      const res = await createUrlAsset(body)
      if (!res.ok) {
        setLinkFormError(
          res.status === 409 ? 'That slug is already in use.'
          : res.status === 422 ? 'Link URL must be a valid https:// address.'
          : 'Could not add link. Please try again.',
        )
        return
      }
      setShowLinkForm(false); resetLinkForm(); refresh()
    } finally {
      setLinkSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteAsset(id); refresh()
    } catch {
      // silent
    }
  }

  const visible = useMemo(
    () => assets.filter(a => matchesAssetSearch(a, search)),
    [assets, search],
  )

  return (
    <PageFrame width="list" className="space-y-4">
      <PageHeader
        title="Assets"
        icon={FileImage}
        sticky
        subtitle={`${assets.length} ${assets.length === 1 ? 'asset' : 'assets'}`}
        actions={
          <>
            <button
              onClick={() => { resetForm(); setShowForm(!showForm); setShowLinkForm(false) }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              <Plus className="w-3.5 h-3.5" /> Add Asset
            </button>
            <button
              onClick={() => { resetLinkForm(); setShowLinkForm(!showLinkForm); setShowForm(false) }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              <LinkIcon className="w-3.5 h-3.5" /> Add Link
            </button>
          </>
        }
      />

      <div className="space-y-4">
      {showForm && (
        <div className="bg-muted rounded-lg p-4 border border-border space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            New Asset
          </h3>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">File *</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Product Brochure"
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Slug *</label>
            <input
              value={slug}
              onChange={e => setSlug(e.target.value)}
              placeholder="e.g. product-brochure"
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <AttributeChipInput label="Tags" inputId="asset-tags"
            values={tags} onChange={setTags}
            placeholder="e.g. brochure, menu — helps search and helps the AI pick the right file" />
          {formError && (
            <p className="text-xs text-destructive">{formError}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowForm(false); resetForm() }}
              className="px-3 py-1.5 text-sm text-muted-foreground"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={saving || !file || !name || !slug}
              className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      {showLinkForm && (
        <div className="bg-muted rounded-lg p-4 border border-border space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            New Link
          </h3>
          <div>
            <label htmlFor="link-name" className="block text-xs font-medium text-muted-foreground mb-1">Link Name *</label>
            <input
              id="link-name"
              value={linkName}
              onChange={e => setLinkName(e.target.value)}
              placeholder="e.g. Order on Uber Eats"
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <div>
            <label htmlFor="link-slug" className="block text-xs font-medium text-muted-foreground mb-1">Link Slug *</label>
            <input
              id="link-slug"
              value={linkSlug}
              onChange={e => setLinkSlug(e.target.value)}
              placeholder="e.g. uber-eats-order"
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <div>
            <label htmlFor="link-url" className="block text-xs font-medium text-muted-foreground mb-1">Link URL *</label>
            <input
              id="link-url"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <div>
            <label htmlFor="link-description" className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
            <input
              id="link-description"
              value={linkDescription}
              onChange={e => setLinkDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
            />
          </div>
          <AttributeChipInput label="Tags" inputId="link-tags"
            values={linkTags} onChange={setLinkTags}
            placeholder="e.g. delivery, uber-eats — helps search and helps the AI pick the right link" />
          {linkFormError && (
            <p className="text-xs text-destructive">{linkFormError}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowLinkForm(false); resetLinkForm() }}
              className="px-3 py-1.5 text-sm text-muted-foreground"
            >
              Cancel
            </button>
            <button
              onClick={handleAddLink}
              disabled={linkSaving || !linkName || !linkSlug || !linkUrl}
              className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {linkSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {!isLoading && assets.length > 0 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, slug, or tag"
            className="w-full max-w-sm pl-8 pr-3 py-1.5 text-sm rounded-sm border border-border bg-background"
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : assets.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No assets yet</p>
      ) : visible.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No assets match your search</p>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-xs text-muted-foreground uppercase tracking-wider">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map(a => (
                <tr
                  key={a.id}
                  onClick={() => { if (a.kind !== 'url') setPreviewAsset(a) }}
                  className={a.kind === 'url' ? 'hover:bg-muted' : 'hover:bg-muted cursor-pointer'}
                >
                  <td className="px-4 py-3 font-medium text-foreground">
                    {a.name}
                    {a.kind === 'url' && (
                      <span className="ml-1.5 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                        🔗
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{a.slug}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.mimeType}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatBytes(a.sizeBytes)}</td>
                  <td className="px-4 py-3">
                    {a.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {a.tags.map(t => (
                          <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {a.kind !== 'url' && (
                        <button
                          aria-label={`Preview ${a.name}`}
                          onClick={e => { e.stopPropagation(); setPreviewAsset(a) }}
                          className="p-1 hover:bg-muted-foreground/10 rounded-sm"
                        >
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      )}
                      <button
                        aria-label={`Edit ${a.name}`}
                        onClick={e => { e.stopPropagation(); setEditAsset(a) }}
                        className="p-1 hover:bg-muted-foreground/10 rounded-sm"
                      >
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button
                        aria-label={`Delete ${a.name}`}
                        onClick={e => { e.stopPropagation(); handleDelete(a.id) }}
                        className="p-1 hover:bg-destructive/10 rounded-sm"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
      <AssetPreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      <AssetEditDrawer
        asset={editAsset}
        onClose={() => setEditAsset(null)}
        onSaved={() => { setEditAsset(null); refresh() }}
      />
    </PageFrame>
  )
}
